import React, { useEffect, useState } from 'react';
import { useAuthStore, hasMinimumRole, ROLES, getRoleLabel } from '../lib/auth';
import { userAPI, venueAPI, inviteAPI, CAPABILITIES } from '../lib/api';
import KioskRegisterButton from '../components/ui/KioskRegisterButton';
import { 
  Users, 
  Shield,
  Building2,
  Loader2,
  AlertCircle,
  Search,
  Settings,
  AlertTriangle,
  CheckCircle2,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errorMessage';

const InviteStaffModal = ({ open, onOpenChange, venues, currentUser, onInviteSent }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    venue_id: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const roleOptions = currentUser?.role === ROLES.GM
    ? [ROLES.STAFF, ROLES.MANAGER]
    : [ROLES.STAFF, ROLES.MANAGER, ROLES.GM];

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.first_name.trim()) nextErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) nextErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) nextErrors.email = 'Email is required';
    if (!formData.role) nextErrors.role = 'Role is required';
    if (!formData.venue_id) nextErrors.venue_id = 'Venue is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await inviteAPI.send(formData);
      toast.success('Invitation sent successfully');
      onInviteSent();
      onOpenChange(false);
      setFormData({ first_name: '', last_name: '', email: '', role: '', venue_id: '' });
      setErrors({});
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send invite'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label className="mb-2 block">First Name *</Label>
            <Input
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="First name"
              className="input-rtb w-full"
              data-testid="invite-first-name"
            />
            {errors.first_name && <p className="text-[#FF3B30] text-xs mt-1">{errors.first_name}</p>}
          </div>
          <div>
            <Label className="mb-2 block">Last Name *</Label>
            <Input
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Last name"
              className="input-rtb w-full"
              data-testid="invite-last-name"
            />
            {errors.last_name && <p className="text-[#FF3B30] text-xs mt-1">{errors.last_name}</p>}
          </div>
          <div>
            <Label className="mb-2 block">Email *</Label>
            <Input
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Email address"
              type="email"
              className="input-rtb w-full"
              data-testid="invite-email"
            />
            {errors.email && <p className="text-[#FF3B30] text-xs mt-1">{errors.email}</p>}
          </div>
          <div>
            <Label className="mb-2 block">Role *</Label>
            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
              <SelectTrigger className="input-rtb" data-testid="invite-role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
                {roleOptions.map((role) => (
                  <SelectItem key={role} value={role} className="text-white">
                    {getRoleLabel(role)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && <p className="text-[#FF3B30] text-xs mt-1">{errors.role}</p>}
          </div>
          <div>
            <Label className="mb-2 block">Venue *</Label>
            <Select value={formData.venue_id} onValueChange={(value) => setFormData({ ...formData, venue_id: value })}>
              <SelectTrigger className="input-rtb" data-testid="invite-venue">
                <SelectValue placeholder="Select venue" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
                {venues.map((venue) => (
                  <SelectItem key={venue.id} value={venue.id} className="text-white">
                    {venue.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.venue_id && <p className="text-[#FF3B30] text-xs mt-1">{errors.venue_id}</p>}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="btn-rtb-primary" disabled={submitting} data-testid="send-invite-btn">
              {submitting ? 'Sending…' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// User Capabilities Component - Only visible to Super Admin and Ops
const UserCapabilities = ({ user: targetUser, onUpdate }) => {
  const { user: currentUser } = useAuthStore();
  const [permissions, setPermissions] = useState(targetUser.custom_permissions || []);
  const [updating, setUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Only Super Admin and Ops can see this section
  const canManageCapabilities = currentUser?.role === ROLES.SUPER_ADMIN || currentUser?.role === ROLES.OPS;
  
  if (!canManageCapabilities) {
    return null;
  }

  const handleToggle = (capabilityId) => {
    setPermissions(prev => {
      const newPerms = prev.includes(capabilityId)
        ? prev.filter(p => p !== capabilityId)
        : [...prev, capabilityId];
      setHasChanges(true);
      return newPerms;
    });
  };

  const handleSave = async () => {
    setUpdating(true);
    try {
      await userAPI.updatePermissions(targetUser.id, permissions);
      toast.success('Capabilities updated');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update capabilities'));
    } finally {
      setUpdating(false);
    }
  };

  const getRiskStyles = (risk) => {
    const styles = {
      low: { bg: 'bg-[#00E676]/10', border: 'border-[#00E676]/30', text: 'text-[#00E676]' },
      medium: { bg: 'bg-[#FFB300]/10', border: 'border-[#FFB300]/30', text: 'text-[#FFB300]' },
      high: { bg: 'bg-[#FF3B30]/10', border: 'border-[#FF3B30]/30', text: 'text-[#FF3B30]' },
    };
    return styles[risk] || styles.low;
  };

  return (
    <div className="mt-6 pt-6 border-t border-[#3F3F46]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-[#FF6600]" />
          <h4 className="font-heading text-lg font-bold text-white uppercase">User Capabilities</h4>
        </div>
        {hasChanges && (
          <Button
            onClick={handleSave}
            disabled={updating}
            className="btn-rtb-primary h-9 text-sm"
            data-testid={`save-capabilities-${targetUser.id}`}
          >
            {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </Button>
        )}
      </div>
      
      <p className="text-[#A1A1AA] text-sm mb-4">
        Override or extend standard role permissions for this user.
      </p>

      <div className="space-y-3">
        {CAPABILITIES.map((capability) => {
          const isEnabled = permissions.includes(capability.id);
          const riskStyles = getRiskStyles(capability.risk);
          
          return (
            <div 
              key={capability.id}
              className={`p-4 rounded-md border ${isEnabled ? riskStyles.border : 'border-[#3F3F46]'} ${isEnabled ? riskStyles.bg : 'bg-[#27272A]'} transition-all`}
              data-testid={`capability-${capability.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium">{capability.label}</span>
                    {capability.risk === 'high' && (
                      <span className="px-2 py-0.5 text-xs font-bold uppercase bg-[#FF3B30]/20 text-[#FF3B30] rounded">
                        High Risk
                      </span>
                    )}
                    {capability.risk === 'medium' && (
                      <span className="px-2 py-0.5 text-xs font-bold uppercase bg-[#FFB300]/20 text-[#FFB300] rounded">
                        Medium
                      </span>
                    )}
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{capability.description}</p>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={() => handleToggle(capability.id)}
                  className="data-[state=checked]:bg-[#FF6600]"
                  data-testid={`toggle-${capability.id}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// User Detail Dialog Component
const UserDetailDialog = ({ userData, venues, currentUser, onUpdate, open, onOpenChange }) => {
  const [selectedVenues, setSelectedVenues] = useState(userData?.venue_ids || []);
  const [updating, setUpdating] = useState(false);

  const canEditRole = currentUser?.role === ROLES.SUPER_ADMIN || currentUser?.role === ROLES.OPS;
  const canEditVenues = hasMinimumRole(currentUser, ROLES.GM);
  const canManageCapabilities = currentUser?.role === ROLES.SUPER_ADMIN || currentUser?.role === ROLES.OPS;

  useEffect(() => {
    if (userData) {
      setSelectedVenues(userData.venue_ids || []);
    }
  }, [userData]);

  const handleRoleChange = async (newRole) => {
    setUpdating(true);
    try {
      await userAPI.updateRole(userData.id, newRole);
      toast.success('Role updated');
      onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update role'));
    } finally {
      setUpdating(false);
    }
  };

  const handleVenuesUpdate = async () => {
    setUpdating(true);
    try {
      await userAPI.updateVenues(userData.id, selectedVenues);
      toast.success('Venues updated');
      onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update venues'));
    } finally {
      setUpdating(false);
    }
  };

  const toggleVenue = (venueId) => {
    setSelectedVenues(prev => 
      prev.includes(venueId) 
        ? prev.filter(id => id !== venueId)
        : [...prev, venueId]
    );
  };

  if (!userData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-[#3F3F46] text-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF6600] flex items-center justify-center">
              <span className="text-white font-bold">
                {userData.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            {userData.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* User Info */}
          <div className="card-rtb !p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[#A1A1AA] text-xs uppercase mb-1">Email</p>
                <p className="text-white">{userData.email}</p>
              </div>
              <div>
                <p className="text-[#A1A1AA] text-xs uppercase mb-1">Status</p>
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                  userData.is_active ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'
                }`}>
                  {userData.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Role Section */}
          <div>
            <Label className="text-sm font-bold uppercase text-[#A1A1AA] mb-2 block">
              <Shield className="w-4 h-4 inline mr-2" />
              Role
            </Label>
            {canEditRole && userData.id !== currentUser.id ? (
              <Select value={userData.role} onValueChange={handleRoleChange} disabled={updating}>
                <SelectTrigger className="input-rtb" data-testid={`role-select-${userData.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
                  {Object.values(ROLES).map(role => (
                    <SelectItem key={role} value={role} className="text-white">
                      {getRoleLabel(role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="input-rtb flex items-center px-3">
                {getRoleLabel(userData.role)}
                {userData.id === currentUser.id && (
                  <span className="ml-2 text-xs text-[#52525B]">(You)</span>
                )}
              </div>
            )}
          </div>

          {/* Venues Section */}
          {canEditVenues && (
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA] mb-2 block">
                <Building2 className="w-4 h-4 inline mr-2" />
                Assigned Venues
              </Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {venues.map(venue => (
                  <button
                    key={venue.id}
                    onClick={() => toggleVenue(venue.id)}
                    className={`w-full p-3 rounded-md flex items-center justify-between transition-colors ${
                      selectedVenues.includes(venue.id) 
                        ? 'bg-[#FF6600]/20 border border-[#FF6600]' 
                        : 'bg-[#27272A] hover:bg-[#3F3F46] border border-transparent'
                    }`}
                    data-testid={`venue-toggle-${venue.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className={`w-4 h-4 ${selectedVenues.includes(venue.id) ? 'text-[#FF6600]' : 'text-[#A1A1AA]'}`} />
                      <span className="text-white text-sm">{venue.name}</span>
                    </div>
                    {selectedVenues.includes(venue.id) && (
                      <CheckCircle2 className="w-4 h-4 text-[#FF6600]" />
                    )}
                  </button>
                ))}
              </div>
              {JSON.stringify(selectedVenues) !== JSON.stringify(userData.venue_ids || []) && (
                <Button
                  onClick={handleVenuesUpdate}
                  disabled={updating}
                  className="btn-rtb-primary w-full mt-3"
                  data-testid="save-venues-btn"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Venue Changes'}
                </Button>
              )}
            </div>
          )}

          {/* User Capabilities Section - Only for Super Admin and Ops */}
          {canManageCapabilities && (
            <UserCapabilities user={userData} onUpdate={onUpdate} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UserCard = ({ user: userData, venues, currentUser, onUpdate }) => {
  const [showDetail, setShowDetail] = useState(false);

  const canEdit = hasMinimumRole(currentUser, ROLES.GM);
  const permissionCount = userData.custom_permissions?.length || 0;

  return (
    <>
      <div 
        className="card-rtb cursor-pointer hover:border-[#FF6600]/50"
        onClick={() => canEdit && setShowDetail(true)}
        data-testid={`user-card-${userData.id}`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FF6600] flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {userData.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <h3 className="text-white font-semibold">{userData.name}</h3>
              <p className="text-[#52525B] text-sm">{userData.email}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
            userData.is_active ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'
          }`}>
            {userData.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Role & Venues */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[#A1A1AA]">
            <Shield className="w-4 h-4" />
            <span className="text-sm">{getRoleLabel(userData.role)}</span>
          </div>
          <div className="flex items-center gap-2 text-[#A1A1AA]">
            <Building2 className="w-4 h-4" />
            <span className="text-sm">{userData.venue_ids?.length || 0} Venues</span>
          </div>
        </div>

        {/* Custom Permissions Indicator */}
        {permissionCount > 0 && (
          <div className="flex items-center gap-2 pt-3 border-t border-[#3F3F46]">
            <Settings className="w-4 h-4 text-[#FF6600]" />
            <span className="text-[#FF6600] text-sm font-medium">
              {permissionCount} custom {permissionCount === 1 ? 'capability' : 'capabilities'}
            </span>
          </div>
        )}

        {/* Edit hint */}
        {canEdit && (
          <p className="text-[#52525B] text-xs mt-3 text-center">Click to edit</p>
        )}
      </div>

      <UserDetailDialog
        userData={userData}
        venues={venues}
        currentUser={currentUser}
        onUpdate={onUpdate}
        open={showDetail}
        onOpenChange={setShowDetail}
      />
    </>
  );
};

const UsersPage = () => {
  const { user } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const fetchData = async () => {
    try {
      const [usersRes, venuesRes] = await Promise.all([
        userAPI.list(),
        venueAPI.list(),
      ]);
      setUsers(usersRes.data);
      setVenues(venuesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            Users
          </h1>
          <p className="text-[#A1A1AA] mt-1">
            Manage team members, roles, and capabilities
          </p>
        </div>
        {/* Register this device as a venue kiosk (manager+ only) */}
        <div className="flex items-center gap-3">
          {[ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM].includes(user?.role) && (
            <Button onClick={() => setShowInviteModal(true)} className="btn-rtb-primary">
              <Users className="w-4 h-4 mr-2" />
              Invite Team Member
            </Button>
          )}
          <KioskRegisterButton />
        </div>
      </div>

      {/* Info banner for Ops/Admin */}
      {(user?.role === ROLES.SUPER_ADMIN || user?.role === ROLES.OPS) && (
        <div className="p-4 bg-[#FF6600]/10 border border-[#FF6600]/30 rounded-md flex items-start gap-3">
          <Settings className="w-5 h-5 text-[#FF6600] mt-0.5" />
          <div>
            <p className="text-white font-medium">Capability Management Enabled</p>
            <p className="text-[#A1A1AA] text-sm">
              As {getRoleLabel(user?.role)}, you can manage user capabilities by clicking on a user card.
            </p>
          </div>
        </div>
      )}

      <InviteStaffModal
        open={showInviteModal}
        onOpenChange={setShowInviteModal}
        venues={venues}
        currentUser={user}
        onInviteSent={fetchData}
      />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search users..."
          className="input-rtb pl-10"
          data-testid="search-users-input"
        />
      </div>

      {/* Users Grid */}
      {filteredUsers.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredUsers.map(userData => (
            <UserCard 
              key={userData.id} 
              user={userData} 
              venues={venues}
              currentUser={user}
              onUpdate={fetchData}
            />
          ))}
        </div>
      ) : (
        <div className="card-rtb text-center py-12">
          <AlertCircle className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
            {searchQuery ? 'No Users Found' : 'No Users Yet'}
          </h3>
          <p className="text-[#A1A1AA]">
            {searchQuery ? 'Try a different search term' : 'Users will appear here once they register'}
          </p>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
